import { useCallback, useEffect, useMemo, useState } from 'react'

import { StytchAPIError, useStytchB2BClient } from '@stytch/react/b2b'
import { useNavigate } from 'react-router-dom'

import {
  Text,
  AuthUI,
  Link,
  GreyGrey100,
  GreyGrey70,
  IndigoIndigo60,
  PrimaryHeader32Regular
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import Spinner from 'components/Loading/Spinner'
import { isLocalAuthBypass } from 'lib/auth/localAuth'
import { redirectToDashboard } from 'lib/auth/session'
import { WorkspaceList } from '../components'
import {
  type DiscoveryTokenType,
  discoveryFlowFor,
  oauthProviderOf,
  toWorkspaces
} from '../discovery'
import { reportAuthFailure } from 'lib/auth/reportAuthFailure'
import { ErrorPage } from './ErrorPage'
import { LinkExpiredPage } from './LinkExpiredPage'
import { PkceExpectedCodeVerifierPage } from './PkceExpectedCodeVerifierPage'
import { PkceMismatchPage } from './PkceMismatchPage'

/** Matches the 45 day session the backend requests for the other flows. */
const SESSION_DURATION_MINUTES = 45 * 24 * 60

interface Workspace {
  id: string
  name: string
  action?: React.ReactNode
}

export const SelectWorkspacePage = () => {
  const navigate = useNavigate()
  const stytch = useStytchB2BClient()
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [intermediateSessionToken, setIntermediateSessionToken] = useState<
    string | null
  >(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLocallyAuthenticated, setIsLocallyAuthenticated] = useState(false)
  const [stytchFlowError, setStytchFlowError] = useState<
    StytchAPIError | Error | null
  >(null)
  const createSessionMutation = useMutation(dashboard20240730Client.postSession)

  const handleSelectWorkspace = useCallback(
    async (organisationId: string, token: string) => {
      if (isLocalAuthBypass()) {
        await stytch.discovery.intermediateSessions.exchange({
          organization_id: organisationId,
          session_duration_minutes: SESSION_DURATION_MINUTES
        })
        setIsLocallyAuthenticated(true)
        return
      }

      await createSessionMutation.mutate({
        discoveryFlow: {
          organisationId,
          intermediateSessionToken: token
        }
      })
    },
    [stytch.discovery]
  )

  const handleDiscoveryFlow = useCallback(
    async (tokenType: DiscoveryTokenType) => {
      const fail = (error: Error) => {
        if (tokenType === 'discovery_oauth') {
          reportAuthFailure(error, `${tokenType}Callback`)
        }
        setStytchFlowError(error)
      }

      const token = new URLSearchParams(window.location.search).get('token')
      if (!token) {
        // Thrown here it would escape the effect's `void` and leave the spinner up.
        fail(new Error('Missing discovery token in query params'))
        return
      }

      const flow = discoveryFlowFor(stytch, tokenType)
      try {
        const response = await flow.authenticate(token)
        const workspacesToOffer = toWorkspaces(
          response.discovered_organizations.filter(flow.canSignInTo)
        )

        if (workspacesToOffer.length === 0) {
          const provider = oauthProviderOf(response)
          navigate(
            provider
              ? `/no-workspace?provider=${encodeURIComponent(provider)}`
              : '/no-workspace'
          )
          return
        }

        // Automatically select the workspace if there is only one
        if (workspacesToOffer.length === 1) {
          void handleSelectWorkspace(
            workspacesToOffer[0].id,
            response.intermediate_session_token
          )
        }

        setWorkspaces(workspacesToOffer)
        setIntermediateSessionToken(response.intermediate_session_token)
        setStytchFlowError(null)
      } catch (error) {
        fail(error as Error)
      } finally {
        setIsLoading(false)
      }
    },
    [handleSelectWorkspace, navigate, stytch]
  )

  // Only the magic link flows can expire in a way the "link expired" page describes; an SSO failure is
  // reported by the generic error page instead.
  const isMagicLink = useMemo(() => {
    const tokenType = new URLSearchParams(window.location.search).get(
      'stytch_token_type'
    )

    return tokenType === 'discovery' || tokenType === 'multi_tenant_magic_links'
  }, [])

  const handleMultiTenantFlow = useCallback(async () => {
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) {
      throw new Error('Missing token type or magic link token in query params')
    }

    try {
      await createSessionMutation.mutateAsync({
        magicLinksFlow: {
          magicLinksToken: token
        }
      })

      setStytchFlowError(null)
    } catch (error) {
      setStytchFlowError(error as Error)
    }
  }, [])

  /**
   * The SSO token names its own organisation, so there is no workspace to pick.
   * `POST /auth/session` redeems it, sets the cookies and creates the Sequence
   * user, as it does for the other flows. If PKCE is ever required, the backend
   * owns the verifier in its own cookie, so nothing changes here. In local
   * mock-auth mode the Stytch stub stands in for the exchange as well, so its
   * fixture token never reaches a real backend.
   */
  const handleSsoFlow = useCallback(async () => {
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) {
      // Thrown here it would escape the effect's `void` and leave the spinner up.
      setStytchFlowError(new Error('Missing SSO token in query params'))
      return
    }

    try {
      if (isLocalAuthBypass()) {
        await stytch.sso.authenticate({
          sso_token: token,
          session_duration_minutes: SESSION_DURATION_MINUTES
        })
        setIsLocallyAuthenticated(true)
      } else {
        await createSessionMutation.mutateAsync({
          ssoFlow: { ssoToken: token }
        })
      }

      setStytchFlowError(null)
    } catch (error) {
      setStytchFlowError(error as Error)
    }
  }, [createSessionMutation.mutateAsync, stytch.sso])

  useEffect(() => {
    const authenticate = () => {
      const stytchTokenType = new URLSearchParams(window.location.search).get(
        'stytch_token_type'
      )

      if (
        stytchTokenType === 'discovery' ||
        stytchTokenType === 'discovery_oauth'
      ) {
        void handleDiscoveryFlow(stytchTokenType)
      } else if (stytchTokenType === 'multi_tenant_magic_links') {
        void handleMultiTenantFlow()
      } else if (stytchTokenType === 'sso') {
        void handleSsoFlow()
      } else {
        setStytchFlowError(
          new StytchAPIError({
            status_code: 400,
            error_type: 'stytch_token_type_not_found',
            error_message: 'Request is missing a valid stytch_token_type param',
            error_url: 'https://stytch.com/docs/api/errors'
          })
        )
      }
    }

    authenticate()
  }, [
    handleDiscoveryFlow,
    handleMultiTenantFlow,
    handleSsoFlow,
    stytch.magicLinks.discovery
  ])

  if (createSessionMutation.data || isLocallyAuthenticated) {
    redirectToDashboard()
    return null
  }

  if (stytchFlowError) {
    if ('error_type' in stytchFlowError) {
      switch (stytchFlowError.error_type) {
        case 'magic_link_not_found':
        case 'unable_to_auth_magic_link':
          return <LinkExpiredPage />
        case 'pkce_mismatch':
          return isMagicLink ? <PkceMismatchPage /> : <ErrorPage />
        case 'pkce_expected_code_verifier':
          return isMagicLink ? <PkceExpectedCodeVerifierPage /> : <ErrorPage />
        default:
          return <ErrorPage />
      }
    }

    return <ErrorPage />
  }

  if (createSessionMutation.isError && isMagicLink) {
    return <LinkExpiredPage />
  }

  if (createSessionMutation.error) {
    return <ErrorPage />
  }

  if (isLoading || createSessionMutation.isPending) {
    return (
      <AuthUI.Section gap="24px">
        <Spinner />
      </AuthUI.Section>
    )
  }

  return (
    <AuthUI.Section gap="24px">
      <Text
        {...PrimaryHeader32Regular}
        textAlign="left"
        color={GreyGrey100}
        marginBottom="0px"
      >
        Select your workspace
      </Text>
      <WorkspaceList>
        {workspaces.map(workspace => (
          <WorkspaceList.Item
            key={workspace.id}
            data-testid={`loginApp.selectWorkspace.${workspace.id}`}
            name={workspace.name}
            action={workspace.action}
            onClick={() => {
              void handleSelectWorkspace(
                workspace.id,
                intermediateSessionToken ?? ''
              )
            }}
            link="#"
          />
        ))}
      </WorkspaceList>
      <Text
        textAlign="left"
        fontSize="14px"
        lineHeight="16px"
        color={GreyGrey70}
        zIndex={10}
      >
        Can't find your workspace?&nbsp;
        <Link
          color={IndigoIndigo60}
          to="https://www.sequencehq.com/book-a-demo"
        >
          Book a demo
        </Link>
      </Text>
    </AuthUI.Section>
  )
}

export default SelectWorkspacePage
